import React, { useMemo } from 'react'
import { ReactComponent as CheckmarkIcon } from '@assets/images/Icons/Checkmark.svg'
import type { StepperProps } from './Stepper.types'
import {
  getStepperConnectorTone,
  getStepperStepStatus,
  normalizeStepperSteps,
  STEPPER_DEMO_STEPS,
} from './Stepper.types'
import './Stepper.css'

const Stepper: React.FC<StepperProps> = ({
  steps = STEPPER_DEMO_STEPS,
  currentStep = 0,
  orientation = 'horizontal',
  className = '',
  darkMode = false,
  previewStep,
  onStepClick,
}) => {
  const isPreview = previewStep != null
  const resolvedCurrentStep = isPreview ? previewStep - 1 : currentStep
  const normalizedSteps = useMemo(() => normalizeStepperSteps(steps), [steps])
  const isVertical = orientation === 'vertical'

  const classNames = [
    'ds-stepper',
    isVertical ? 'ds-stepper--vertical' : '',
    darkMode ? 'ds-stepper--dark' : '',
    isPreview ? 'ds-stepper--preview' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <nav className={classNames} aria-label="Progress">
      {normalizedSteps.map((step, index) => {
        const status = getStepperStepStatus(index, resolvedCurrentStep)
        const connectorTone =
          index < normalizedSteps.length - 1
            ? getStepperConnectorTone(index, resolvedCurrentStep)
            : null

        return (
          <React.Fragment key={`${step.label}-${index}`}>
            <div className={`ds-stepper__step ds-stepper__step--${status}`}>
              <div className="ds-stepper__indicator" aria-hidden="true">
                {status === 'completed' ? (
                  <CheckmarkIcon
                    className="ds-stepper__check"
                    width={24}
                    height={24}
                    aria-hidden="true"
                  />
                ) : (
                  <span className="ds-stepper__number">{index + 1}</span>
                )}
              </div>
              {onStepClick && !isPreview ? (
                <button
                  type="button"
                  className="ds-stepper__label-btn"
                  aria-current={status === 'active' ? 'step' : undefined}
                  onClick={() => onStepClick(index)}
                >
                  {step.label}
                </button>
              ) : (
                <span
                  className="ds-stepper__label"
                  aria-current={status === 'active' ? 'step' : undefined}
                >
                  {step.label}
                </span>
              )}
            </div>

            {connectorTone ? (
              <div
                className={`ds-stepper__connector${
                  isVertical ? ' ds-stepper__connector--vertical' : ''
                } ds-stepper__connector--${connectorTone}`}
                aria-hidden="true"
              />
            ) : null}
          </React.Fragment>
        )
      })}
    </nav>
  )
}

export default Stepper
